<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'

import HomeCardGrid from '@/catalog/HomeCardGrid.vue'

import { logout } from '@/auth/api'
import { useAuth } from '@/auth/useAuth'

const router = useRouter()
const auth = useAuth()

interface SandboxCard {
  title: string
  description: string
  route: string
  icon: string
}

const cards = computed<SandboxCard[]>(() => {
  const base: SandboxCard[] = [
    {
      title: 'Agents',
      description:
        'Tester les agents conversationnels Sapiendo : agent de prospection, conseil retraite expert et préparation de rendez-vous PER.',
      route: '/agents',
      icon: '🤖',
    },
  ]
  if (auth.isAdmin.value) {
    base.push({
      title: 'Base de Connaissance Sapiendo',
      description:
        "Tester la recherche vectorielle RAG. Saisir une question retraite et visualiser les chunks retournés et le contexte injecté dans l'agent.",
      route: '/vue/knowledge',
      icon: '📚',
    })
    base.push({
      title: 'Évaluation',
      description:
        "Mesurer la qualité des agents : LLM-as-Judge (benchmark contre dataset expert) et évaluation déterministe de l'extraction du Gathering.",
      route: '/eval',
      icon: '📊',
    })
    base.push({
      title: 'Administration',
      description:
        "Gestion des utilisateurs, demandes d'accès en attente, droits par agent et mémoire long-terme.",
      route: '/admin',
      icon: '🛡️',
    })
    base.push({
      title: 'Feedbacks',
      description:
        'Avis des conseillers sur les conversations : notes Pertinence / Fiabilité / Utilité et commentaires, avec filtres et pagination.',
      route: '/feedbacks',
      icon: '📋',
    })
  }
  return base
})

function navigate(card: SandboxCard): void {
  router.push(card.route)
}

async function onLogout(): Promise<void> {
  await logout()
  await router.push('/login')
}
</script>

<template>
  <div class="home">
    <header class="home-header">
      <div class="home-header-content">
        <h1 class="home-title">Sapiendo Sandbox</h1>
        <p class="home-subtitle">Bac à sable des agents et outils Sapiendo</p>
      </div>
      <div v-if="auth.user.value" class="home-user">
        <span>{{ auth.user.value.email }}</span>
        <button class="logout-btn" @click="onLogout">Déconnexion</button>
      </div>
    </header>

    <main class="home-main">
      <HomeCardGrid :cards="cards" @open="navigate" />
    </main>

    <footer class="home-footer">
      <p>Sapiendo &mdash; Socle technique agent IA</p>
    </footer>
  </div>
</template>

<style scoped>
.home {
  min-height: 100%;
  display: flex;
  flex-direction: column;
}

.home-header {
  background: linear-gradient(135deg, #1e40af 0%, #2563eb 50%, #3b82f6 100%);
  color: #ffffff;
  padding: 3rem 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
}

.home-user {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.9rem;
}

.logout-btn {
  padding: 0.4rem 0.85rem;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
}

.logout-btn:hover {
  background: rgba(255, 255, 255, 0.25);
}

.home-header-content {
  max-width: 960px;
  margin: 0 auto;
}

.home-title {
  font-size: 2rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  margin-bottom: 0.25rem;
}

.home-subtitle {
  font-size: 1.125rem;
  opacity: 0.85;
  font-weight: 400;
}

.home-main {
  flex: 1;
  max-width: 960px;
  width: 100%;
  margin: 0 auto;
  padding: 2rem;
}

.home-footer {
  text-align: center;
  padding: 1.5rem 2rem;
  font-size: 0.8125rem;
  color: #9ca3af;
}
</style>
